import { forwardRef, useState, useEffect } from 'react';

// material-ui
import {
    Avatar,
    Button,
    FormHelperText,
    FormControl,
    Grid,
    Stack,
    TextField,
    InputLabel,
    InputAdornment,
    Select,
    MenuItem,
    IconButton,
    DialogContent,
    Dialog,
    Zoom
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import HighlightOffTwoToneIcon from '@mui/icons-material/HighlightOffTwoTone';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import useApi from 'hooks/useApi';
import VenueService from 'services/VenueService';
import UserService from 'services/UserService';
import userCreateSchema from 'utils/schema/UserCreateSchema';
import userEditSchema from 'utils/schema/UserEditSchema';
import { gridSpacing, USER_TYPE, GENDER, IMAGE_FORMAT } from 'store/constant';

// assets
import DefaultImage from 'assets/images/users/default_user.png';

// third party
import styled from '@emotion/styled';
import moment from 'moment';
import { useFormik } from 'formik';

const Transition = forwardRef((props, ref) => <Zoom ref={ref} {...props} />);

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

export const CreateUser = ({ open, handleClose, selectedUser, userType, setReload, venueUuid, venueArray }) => {

    const [previewImage, setPreviewImage] = useState(DefaultImage);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false)

    const handleImageChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile.size > 5000000) { // 5mb
            setError('Image should be in 5mb.');
        } else {
            const typesplit = selectedFile.type.split('/');
            if (IMAGE_FORMAT.includes(typesplit[typesplit.length - 1])) {
                setError('');
                const formData = new FormData();
                formData.append('userImage', selectedFile);
                uploadFileApi.execute(formData);
            } else {
                setError('File format not supported');
            }
        }
        e.target.value = null;
        e.target.files = null;
    };

    const updateUserInfo = () => {
        let formikValues = { ...formik.values };
        if (formikValues.dob && formikValues.dob.toString() !== 'Invalid date') {
            formikValues.dob = moment(formikValues.dob, 'ddd MMM DD YYYY').format('YYYY-MM-DD');
        } else {
            delete formikValues.dob;
        }
        if (userType !== USER_TYPE[0].value) {
            delete formikValues.uuid_venue;
        }
        if (userType !== USER_TYPE[0].value || selectedUser) {
            delete formikValues.password;
        }
        delete formikValues.confirm_password;
        if (selectedUser) {
            updateUserApi.execute(formikValues, selectedUser.uuid);
        } else {
            createUserApi.execute(formikValues);
        }
    };

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const setFormValue = (selectedUser = {}) => {
        formik.setFieldValue('firstname', selectedUser.firstname ? selectedUser.firstname : '');
        formik.setFieldValue('lastname', selectedUser.lastname ? selectedUser.lastname : '');
        formik.setFieldValue('email', selectedUser.email ? selectedUser.email : '');
        formik.setFieldValue('phone', selectedUser.phone ? selectedUser.phone : '');
        formik.setFieldValue('countrycode', selectedUser.countrycode ? selectedUser.countrycode : 91);
        formik.setFieldValue('loc_city', selectedUser.loc_city ? selectedUser.loc_city : '');
        formik.setFieldValue('dob', selectedUser.dob ? moment(selectedUser.dob).format('ddd MMM DD YYYY') : null);
        formik.setFieldValue('type', selectedUser.type ? selectedUser.type : '');
        formik.setFieldValue('uuid_venue', selectedUser.uuid_venue ? selectedUser.uuid_venue : []);
        formik.setFieldValue('gender', selectedUser.gender ? selectedUser.gender : '');
        formik.setFieldValue('image', selectedUser.image ? selectedUser.image : '');
        setPreviewImage(selectedUser.image ? selectedUser.image : DefaultImage);
    };

    const closeDialog = (selectedUser) => {
        setPreviewImage(selectedUser.image ? selectedUser.image : DefaultImage);
        formik.resetForm();
        handleClose();
    };

    const uploadFileHandler = (response) => {
        let data = response.data;
        formik.setFieldValue('image', data.s3_path);
        setPreviewImage(data.s3_path);
    };

    const updateUserHandler = () => {
        formik.resetForm()
        setReload(r => !r);
        handleClose();
    };

    const uploadFileApi = useApi(VenueService.uploadFile, uploadFileHandler);
    const createUserApi = useApi(UserService.createUser, updateUserHandler, true);
    const updateUserApi = useApi(UserService.updateUserByUuid, updateUserHandler, true);

    const formik = useFormik({
        initialValues: {
            firstname: '',
            lastname: '',
            email: '',
            phone: '',
            countrycode: 91,
            loc_city: '',
            dob: null,
            type: userType,
            uuid_venue: venueUuid ? [venueUuid] : [],
            gender: '',
            image: '',
            ...(userType !== USER_TYPE[0].value || selectedUser ? {} : {
                password: '',
                confirm_password: ''
            })
        },
        validationSchema: selectedUser ? userEditSchema : userCreateSchema,
        onSubmit: updateUserInfo
    });

    useEffect(() => {
        if (selectedUser) {
            setFormValue(selectedUser);
        }
    }, [selectedUser]);

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            keepMounted
            onClose={closeDialog}
            fullWidth
            maxWidth="sm"
            aria-labelledby="alert-dialog-slide-title"
            aria-describedby="alert-dialog-slide-description"
        >
            <DialogContent sx={{ p: 0 }}>
                <MainCard
                    title={`${selectedUser ? 'Edit' : 'Create'} ${userType === USER_TYPE[0].value ? 'Manager' : 'Customer'}`}
                    secondary={
                        <IconButton onClick={closeDialog} size="large" aria-label="Close User">
                            <HighlightOffTwoToneIcon fontSize="small" />
                        </IconButton>
                    }>
                    <Grid container spacing={gridSpacing}>
                        <Grid item sm={6} md={12}>

                            <Grid container spacing={0.5} sx={{ marginBottom: 2 }}>
                                <Grid item xs={12}>
                                    <Avatar alt="User 1" src={previewImage} sx={{ width: 120, height: 120, margin: '0 auto' }} />
                                </Grid>
                                <Grid item xs={12}>
                                    <FormHelperText error sx={{ textAlign: 'center' }} id="standard-weight-helper-text-profile-user">
                                        {error}
                                    </FormHelperText>
                                </Grid>
                                <Grid item xs={12} sx={{ textAlign: 'center' }}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        component='label'
                                        role='undefined'
                                        onChange={handleImageChange}
                                    >
                                        Upload Avatar
                                        <VisuallyHiddenInput type='file' />
                                    </Button>
                                </Grid>
                            </Grid>
                            <Grid container spacing={gridSpacing}>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        label="First Name*"
                                        name="firstname"
                                        error={Boolean(formik.touched.firstname && formik.errors.firstname)}
                                        value={formik.values.firstname}
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                    />
                                    {formik.touched.firstname && formik.errors.firstname && (
                                        <FormHelperText error id="standard-weight-helper-text-firstname-user">
                                            {formik.errors.firstname}
                                        </FormHelperText>
                                    )}
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        label="Last Name"
                                        name="lastname"
                                        error={Boolean(formik.touched.lastname && formik.errors.lastname)}
                                        value={formik.values.lastname}
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                    />
                                    {formik.touched.lastname && formik.errors.lastname && (
                                        <FormHelperText error id="standard-weight-helper-text-lastname-user">
                                            {formik.errors.lastname}
                                        </FormHelperText>
                                    )}
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        label="Email*"
                                        name="email"
                                        error={Boolean(formik.touched.email && formik.errors.email)}
                                        value={formik.values.email}
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                    />
                                    {formik.touched.email && formik.errors.email && (
                                        <FormHelperText error id="standard-weight-helper-text-email-user">
                                            {formik.errors.email}
                                        </FormHelperText>
                                    )}
                                </Grid>
                                <Grid item md={6} xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Phone*"
                                        name="phone"
                                        error={Boolean(formik.touched.phone && formik.errors.phone)}
                                        value={formik.values.phone}
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                    />
                                    {formik.touched.phone && formik.errors.phone && (
                                        <FormHelperText error id="standard-weight-helper-text-phone-user">
                                            {formik.errors.phone}
                                        </FormHelperText>
                                    )}
                                </Grid>
                                {
                                    formik.values.type !== USER_TYPE[0].value ? (<></>) : (
                                        <Grid item xs={6}>
                                            <FormControl captionLabel='Venue' fullWidth size="medium">
                                                <InputLabel id="venue-select-label">Venue*</InputLabel>
                                                <Select
                                                    labelId="venue-select-label"
                                                    id="venue-select"
                                                    label="Venue*"
                                                    name="uuid_venue"
                                                    multiple
                                                    error={Boolean(formik.touched.uuid_venue && formik.errors.uuid_venue)}
                                                    value={formik.values.uuid_venue}
                                                    onBlur={formik.handleBlur}
                                                    onChange={formik.handleChange}
                                                >
                                                    {
                                                        venueArray.map((venue, index) => {
                                                            return (
                                                                <MenuItem key={index} value={venue.uuid}>{venue.venue_name}</MenuItem>
                                                            )
                                                        })
                                                    }
                                                </Select>
                                            </FormControl>
                                            {formik.touched.uuid_venue && formik.errors.uuid_venue && (
                                                <FormHelperText error id="standard-weight-helper-text-uuid_venue-user">
                                                    {formik.errors.uuid_venue}
                                                </FormHelperText>
                                            )}
                                        </Grid>
                                    )
                                }
                                <Grid item md={6} xs={12}>
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DatePicker
                                            label="DOB"
                                            views={["year", "month", "day"]}
                                            inputFormat="dd-MM-yyyy"
                                            onChange={(date) => formik.setFieldValue("dob", date ? date : "")}
                                            onBlur={formik.handleBlur}
                                            disableFuture
                                            value={formik.values.dob}
                                            renderInput={(params) => <TextField fullWidth name="dob" onBlur={formik.handleBlur}
                                                value={formik.values.dob} onChange={formik.handleChange} onKeyDown={(e) => { e.preventDefault(); }} size="medium" {...params} />}
                                        />
                                    </LocalizationProvider>
                                    {formik.touched.dob && formik.errors.dob && (
                                        <FormHelperText error id="standard-weight-helper-date-dob-user">
                                            {formik.errors.dob}
                                        </FormHelperText>
                                    )}
                                </Grid>
                                <Grid item md={6} xs={12}>
                                    <FormControl captionLabel='Gender' fullWidth size="medium">
                                        <InputLabel id="gender-select-label">Gender*</InputLabel>
                                        <Select
                                            labelId="gender-select-label"
                                            id="gender-select"
                                            label="Gender*"
                                            name="gender"
                                            error={Boolean(formik.touched.gender && formik.errors.gender)}
                                            value={formik.values.gender}
                                            onBlur={formik.handleBlur}
                                            onChange={formik.handleChange}
                                        >
                                            {
                                                GENDER.map((gender, index) => {
                                                    return (
                                                        <MenuItem key={index} value={gender.value}>{gender.name}</MenuItem>
                                                    )
                                                })
                                            }
                                        </Select>
                                    </FormControl>
                                    {formik.touched.gender && formik.errors.gender && (
                                        <FormHelperText error id="standard-weight-helper-text-gender-user">
                                            {formik.errors.gender}
                                        </FormHelperText>
                                    )}
                                </Grid>
                                <Grid item md={6} xs={12}>
                                    <TextField
                                        fullWidth
                                        label="City*"
                                        name="loc_city"
                                        error={Boolean(formik.touched.loc_city && formik.errors.loc_city)}
                                        value={formik.values.loc_city}
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                    />
                                    {formik.touched.loc_city && formik.errors.loc_city && (
                                        <FormHelperText error id="standard-weight-helper-text-loc_city-user">
                                            {formik.errors.loc_city}
                                        </FormHelperText>
                                    )}
                                </Grid>
                                {
                                    (userType !== USER_TYPE[0].value || selectedUser) ? (<></>) : (
                                        <>
                                            <Grid item md={6} xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Password*"
                                                    type="password"
                                                    name="password"
                                                    error={Boolean(formik.touched.password && formik.errors.password)}
                                                    value={formik.values.password}
                                                    onBlur={formik.handleBlur}
                                                    onChange={formik.handleChange}
                                                    autoComplete='off'
                                                />
                                                {formik.touched.password && formik.errors.password && (
                                                    <FormHelperText error id="standard-weight-helper-text-password-user">
                                                        {formik.errors.password}
                                                    </FormHelperText>
                                                )}
                                            </Grid>
                                            <Grid item md={6} xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Confirm Password*"
                                                    type={showPassword ? "text" : "password"}
                                                    name="confirm_password"
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment
                                                                position='end'
                                                            >
                                                                <IconButton
                                                                    aria-label="toggle password visibility"
                                                                    edge="end"
                                                                    onClick={handleClickShowPassword}
                                                                >{showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        )
                                                    }}
                                                    error={Boolean(formik.touched.confirm_password && formik.errors.confirm_password)}
                                                    value={formik.values.confirm_password}
                                                    onBlur={formik.handleBlur}
                                                    onChange={formik.handleChange}
                                                />
                                                {formik.touched.confirm_password && formik.errors.confirm_password && (
                                                    <FormHelperText error id="standard-weight-helper-text-confirm-password-user">
                                                        {formik.errors.confirm_password}
                                                    </FormHelperText>
                                                )}
                                            </Grid>
                                        </>
                                    )
                                }
                                <Grid item xs={12}>
                                    <Stack direction="row" sx={{ justifyContent: 'end' }}>
                                        <Button
                                            color="secondary"
                                            variant="contained"
                                            onClick={formik.handleSubmit}
                                        >
                                            {`${selectedUser ? 'Update' : 'Create'} ${USER_TYPE[0].value === userType ? 'Manager' : 'Customer'}`}
                                        </Button>
                                    </Stack>
                                </Grid>
                            </Grid>

                        </Grid>
                    </Grid>
                </MainCard >
            </DialogContent>
        </Dialog >
    );
};
